import { useFormik } from 'formik';
import { Box, Link, Stack, TextField, Typography } from '@mui/material';
import { Layout as AuthLayout } from 'layouts/auth/Layout';
import { signUp } from 'store/actions/authActions';
import { useDispatch, useSelector } from 'react-redux';
import { SignUpValidationSchema } from 'utils/validation';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';

const SignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((store: any) => store.auth);

  useEffect(() => {
    if (auth.authInfo.userSignedUp) {
      navigate('/auth/login');
    }
    if (auth.authInfo.isAuthorized) {
      navigate('/');
    }
  }, [navigate, auth.authInfo?.isAuthorized, auth.authInfo.userSignedUp]);

  const formik = useFormik({
    initialValues: {
      email: '',
      name: '',
      password: '',
      confirm_password: '',
      submit: null,
    },
    validationSchema: SignUpValidationSchema,
    onSubmit: async (values, helpers) => {
      try {
        if (values.password !== values.confirm_password) {
          helpers.setErrors({ submit: 'Please make sure your passwords match' });
          return;
        }
        dispatch(
          signUp({
            userData: {
              email: values.email,
              name: values.name,
              password: values.password,
              confirm_password: values.confirm_password,
            },
          }),
        );
      } catch (err: any) {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
  });

  return (
    <AuthLayout>
      <Box
        sx={{
          flex: '1 1 auto',
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            maxWidth: 550,
            px: 3,
            py: '100px',
            width: '100%',
          }}
        >
          <div>
            <Stack spacing={1} sx={{ mb: 3 }}>
              <Typography variant='h4'>Register</Typography>
              <Typography color='text.secondary' variant='body2'>
                Already have an account? &nbsp;
                <Link href='/auth/login' underline='hover' variant='subtitle2'>
                  Log in
                </Link>
              </Typography>
            </Stack>
            <form noValidate onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  error={!!(formik.touched.name && formik.errors.name)}
                  fullWidth
                  helperText={formik.touched.name && formik.errors.name}
                  label='Name'
                  name='name'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.name}
                />
                <TextField
                  error={!!(formik.touched.email && formik.errors.email)}
                  fullWidth
                  helperText={formik.touched.email && formik.errors.email}
                  label='Email Address'
                  name='email'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type='email'
                  value={formik.values.email}
                />
                <TextField
                  error={!!(formik.touched.password && formik.errors.password)}
                  fullWidth
                  helperText={formik.touched.password && formik.errors.password}
                  label='Password'
                  name='password'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type='password'
                  value={formik.values.password}
                />
                <TextField
                  error={!!(formik.touched.confirm_password && formik.errors.confirm_password)}
                  fullWidth
                  helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                  label='Confirm Password'
                  name='confirm_password'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type='password'
                  value={formik.values.confirm_password}
                />
              </Stack>
              {formik.errors.submit && (
                <Typography color='error' sx={{ mt: 3 }} variant='body2'>
                  {formik.errors.submit}
                </Typography>
              )}
              <LoadingButton
                disabled={auth.authInfo?.isAPIInProgress}
                loading={auth.authInfo?.isAPIInProgress}
                fullWidth
                size='large'
                sx={{ mt: 3 }}
                type='submit'
                variant='contained'
              >
                Register
              </LoadingButton>
            </form>
          </div>
        </Box>
      </Box>
    </AuthLayout>
  );
};

export default SignUp;
